import {
  Admin,
  Resource,
  CustomRoutes,
  localStorageStore,
  StoreContextProvider,
} from 'react-admin';
import { Navigate, Route } from 'react-router-dom';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import englishMessages from './i18n/en';
import authProvider from './auth/authProvider';
import compositeDataProvider from './utils/compositeDataProvider';
import { Layout, Login } from './layout';
import messageInbox from './messageInbox';
import inquiries from './inquiries';
import customerContacts from './customerContacts';
import leadCompanies from './leads';
import marketListingLeads from './marketListingLeads';
import { LeadEnrichmentJobList } from './leads/LeadEnrichmentJobList';
import QueueIcon from '@mui/icons-material/Queue';
import MessageInboxShow from './messageInbox/MessageInboxShow';
import InquiryReplies from './inquiries/InquiryReplies';
import InquiryInboxList from './inquiries/InquiryInboxList';
import InquiryInboxThread from './inquiries/InquiryInboxThread';
import NotAuthorized from './pages/NotAuthorized';
import MarketingHome from './pages/MarketingHome';
import DashboardCustomers from './customers/DashboardCustomers';
import DashboardSessions from './customers/DashboardSessions';
import DashboardFunnels from './customers/DashboardFunnels';
import DashboardLinks from './links/DashboardLinks';
import DashboardActivities from './activities/DashboardActivities';
import DashboardReprocessed from './recycle/DashboardReprocessed';
import DashboardSeo from './seo/DashboardSeo';
import DashboardProductSeo from './seo/DashboardProductSeo';
import WhatsAppDashboard from './messageInbox/WhatsAppDashboard';
import DashboardSocial from './social/DashboardSocial';
import DashboardSellerAcquisition from './social/DashboardSellerAcquisition';
import DashboardApps from './apps/DashboardApps';
import DashboardOrdersValidation from './orders/DashboardOrdersValidation';
import DashboardInvestorCohorts from './investors/DashboardInvestorCohorts';
import DashboardProductAffinity from './customers/DashboardProductAffinity';
import WhatsAppBroadcastList from './whatsappBroadcasts/WhatsAppBroadcastList';
import WhatsAppBroadcastCreate from './whatsappBroadcasts/WhatsAppBroadcastCreate';
import WhatsAppBroadcastShow from './whatsappBroadcasts/WhatsAppBroadcastShow';
import './utils/setupAxiosAuth';

const i18nProvider = polyglotI18nProvider(() => englishMessages, 'en', [
  { locale: 'en', name: 'English' },
]);

const store = localStorageStore(
  process.env.CURRENT_VERSION || '1.0.0',
  process.env.APP_MODULE || 'marketing'
);

const App = () => {
  return (
    <Admin
      title="Credco Marketing"
      store={store}
      dataProvider={compositeDataProvider}
      layout={Layout}
      i18nProvider={i18nProvider}
      authProvider={authProvider}
      loginPage={Login}
      dashboard={MarketingHome}
    >
      <Resource name="messageinbox" {...messageInbox} />
      <Resource name="inquiries" {...inquiries} />
      <Resource name="customercontacts" {...customerContacts} />
      <Resource name="leadcompanies" {...leadCompanies} />
      <Resource name="marketlistingleads" {...marketListingLeads} />
      <Resource
        name="leadenrichmentjobs"
        list={LeadEnrichmentJobList}
        icon={QueueIcon}
        options={{ label: 'Lead Enrichment Queue' }}
      />
      <CustomRoutes>
        <Route path="/not-authorized" element={<NotAuthorized />} />
        <Route path="/messageinbox/:conversationKey/show" element={<MessageInboxShow />} />
        <Route path="/inquiries/:inquiryId/replies" element={<InquiryReplies />} />
        <Route path="/inquiry-inbox" element={<InquiryInboxList />} />
        <Route path="/inquiry-inbox/:threadKey" element={<InquiryInboxThread />} />
        <Route path="/dashboard/whatsapp" element={<WhatsAppDashboard />} />
        <Route path="/dashboard/customers" element={<DashboardCustomers />} />
        <Route path="/dashboard/sessions" element={<DashboardSessions />} />
        <Route path="/dashboard/funnels" element={<DashboardFunnels />} />
        <Route path="/dashboard/orders" element={<DashboardOrdersValidation />} />
        <Route path="/dashboard/cohorts-segmentation" element={<DashboardInvestorCohorts />} />
        <Route path="/dashboard/product-affinity" element={<DashboardProductAffinity />} />
        <Route path="/dashboard/customer-segmentation" element={<Navigate to="/dashboard/cohorts-segmentation" replace />} />
        <Route path="/dashboard/investor-cohorts" element={<Navigate to="/dashboard/cohorts-segmentation" replace />} />
        <Route path="/dashboard/orders-validation" element={<Navigate to="/dashboard/orders" replace />} />
        <Route path="/dashboard/links" element={<DashboardLinks />} />
        <Route path="/dashboard/activities" element={<DashboardActivities />} />
        <Route path="/dashboard/reprocessed" element={<DashboardReprocessed />} />
        <Route path="/dashboard/seo" element={<DashboardSeo />} />
        <Route path="/dashboard/product-seo" element={<DashboardProductSeo />} />
        <Route path="/dashboard/social" element={<DashboardSocial />} />
        <Route path="/dashboard/seller-acquisition" element={<DashboardSellerAcquisition />} />
        <Route path="/dashboard/apps" element={<DashboardApps />} />
        <Route path="/whatsapp-broadcasts" element={<WhatsAppBroadcastList />} />
        <Route path="/whatsapp-broadcasts/new" element={<WhatsAppBroadcastCreate />} />
        <Route path="/whatsapp-broadcasts/:id" element={<WhatsAppBroadcastShow />} />
      </CustomRoutes>
    </Admin>
  );
};

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
);

export default AppWrapper;
